<template>
  <v-card class="mt-4 ncrm-contact-normalization">
    <div class="ncrm-contact-normalization__header">
      <div class="min-w-0">
        <div class="text-subtitle-1 font-weight-semibold ncrm-text-strong">
          {{ tableTitle }}
        </div>
        <div class="text-body-2 text-medium-emphasis">
          {{ $t("ncrm.workflows.contactNormalization.subtitle") }}
        </div>
      </div>
      <div class="d-flex flex-wrap align-center ga-2">
        <v-select
          v-model="statusFilter"
          :items="statusOptions"
          item-title="label"
          item-value="value"
          density="compact"
          hide-details
          class="ncrm-contact-normalization__status-filter"
          :menu-props="{ maxHeight: 320, zIndex: 2700 }"
        />
        <v-btn color="primary" variant="tonal" :loading="scanning" @click="scanNode">
          <v-icon start>mdi-radar</v-icon>
          {{ $t("ncrm.workflows.contactNormalization.actions.scan") }}
        </v-btn>
        <v-btn
          color="success"
          variant="tonal"
          :disabled="!selectedIds.length"
          :loading="applyingBatch"
          @click="applySelected"
        >
          <v-icon start>mdi-check-all</v-icon>
          {{ $t("ncrm.workflows.contactNormalization.actions.applySelected") }}
        </v-btn>
      </div>
    </div>

    <v-alert v-if="localMessage" type="info" density="compact" class="mx-4 mt-3">
      {{ localMessage }}
    </v-alert>
    <v-alert v-if="localError" type="error" density="compact" class="mx-4 mt-3">
      {{ localError }}
    </v-alert>

    <div v-if="selectedIds.length" class="ncrm-contact-normalization__bulk">
      <div class="text-body-2">
        {{ $t("ncrm.workflows.contactNormalization.selectedCount", { count: selectedIds.length }) }}
      </div>
      <v-btn variant="text" size="small" @click="selectedIds = []">
        {{ $t("app.actions.clear") }}
      </v-btn>
    </div>

    <v-data-table-server
      v-model="selectedIds"
      v-model:page="page"
      v-model:items-per-page="perPage"
      :headers="headers"
      :items="records"
      :items-length="total"
      item-key="id"
      item-value="id"
      show-select
      select-strategy="page"
      density="comfortable"
      class="ncrm-contact-normalization__table"
      :loading="loading"
      :items-per-page-options="[10, 25, 50, 100]"
      @click:row="handleRowClick"
    >
      <template #item.company_name="{ item }">
        <div class="d-flex align-center ga-2">
          <v-icon size="16" color="medium-emphasis">mdi-domain</v-icon>
          <span class="text-body-2 ncrm-text-strong">{{ item.company_name || item.entity_id }}</span>
        </div>
      </template>
      <template #item.status="{ item }">
        <v-chip :color="statusColor(item.status)">
          {{ $t(`ncrm.workflows.contactNormalization.status.${item.status}`) }}
        </v-chip>
      </template>
      <template #item.original="{ item }">
        <div class="ncrm-contact-normalization__value-list">
          {{ formatValues(item.original_values) }}
        </div>
      </template>
      <template #item.proposed="{ item }">
        <div class="ncrm-contact-normalization__value-list">
          {{ formatValues(item.proposed_values) }}
        </div>
      </template>
      <template #item.issues="{ item }">
        <v-chip v-if="issueCount(item)" size="x-small" :color="hasReviewIssue(item) ? 'warning' : 'info'">
          {{ issueCount(item) }}
        </v-chip>
        <span v-else class="text-medium-emphasis">—</span>
      </template>
      <template #item.actions="{ item }">
        <div class="d-flex justify-end ga-1">
          <v-btn
            icon="mdi-eye-outline"
            variant="text"
            size="small"
            :aria-label="$t('ncrm.workflows.contactNormalization.actions.openRow')"
            @click.stop="openRecord(item)"
          />
          <v-btn
            icon="mdi-check-circle-outline"
            color="success"
            variant="text"
            size="small"
            :disabled="!canApplyStatus(item.status)"
            :loading="applyingRowId === item.id"
            :aria-label="$t('ncrm.workflows.contactNormalization.actions.applyRow')"
            @click.stop="applyRecord(item)"
          />
        </div>
      </template>
      <template #no-data>
        <v-alert type="info" class="ma-4">
          {{ $t("ncrm.workflows.contactNormalization.empty") }}
        </v-alert>
      </template>
    </v-data-table-server>

    <NcrmContactNormalizationDrawer
      v-model="drawer"
      :record="selectedRecord"
      :node-type="nodeType"
      :loading="loadingRecord"
      :saving="savingRecord"
      :applying="Boolean(applyingRowId)"
      @save="saveCorrection"
      @apply="applyRecord"
      @skip="skipRecord"
    />
  </v-card>
</template>

<script>
import {
  CONTACT_NORMALIZATION_NODE_KEY_TO_TYPE,
  CONTACT_NORMALIZATION_NODE_TYPES,
  WORKFLOW_NODE_KEYS,
} from "@/Utile/ncrmWorkflows.mjs";
import { contactNormalizationQueueActions } from "@/components/ncrm/workflows/NcrmContactNormalizationQueueActions.mjs";
import NcrmContactNormalizationDrawer from "@/components/ncrm/workflows/NcrmContactNormalizationDrawer.vue";

const STATUSES = ["ready", "needs_review", "applied", "skipped", "failed"];
const PHONE_FIELDS = ["phone_primary", "phone_secondary", "phone_tertiary"];
const EMAIL_FIELDS = ["email_primary", "email_secondary", "email_tertiary"];

export default {
  name: "NcrmContactNormalizationQueue",
  components: {
    NcrmContactNormalizationDrawer,
  },
  props: {
    workflowInstanceId: { type: String, required: true },
    nodeKey: { type: String, default: WORKFLOW_NODE_KEYS.PHONE_NORMALIZATION },
  },
  data() {
    return {
      records: [],
      total: 0,
      page: 1,
      perPage: 25,
      statusFilter: "",
      selectedIds: [],
      loading: false,
      scanning: false,
      applyingBatch: false,
      applyingRowId: null,
      drawer: false,
      selectedRecord: null,
      loadingRecord: false,
      savingRecord: false,
      localMessage: "",
      localError: "",
    };
  },
  computed: {
    nodeType() {
      return CONTACT_NORMALIZATION_NODE_KEY_TO_TYPE[this.nodeKey] || CONTACT_NORMALIZATION_NODE_TYPES.PHONE;
    },
    fields() {
      return this.nodeType === CONTACT_NORMALIZATION_NODE_TYPES.EMAIL ? EMAIL_FIELDS : PHONE_FIELDS;
    },
    tableTitle() {
      return this.nodeType === CONTACT_NORMALIZATION_NODE_TYPES.EMAIL
        ? this.$t("ncrm.workflows.contactNormalization.emailTitle")
        : this.$t("ncrm.workflows.contactNormalization.phoneTitle");
    },
    statusOptions() {
      return [
        { value: "", label: this.$t("ncrm.workflows.contactNormalization.status.all") },
        ...STATUSES.map((status) => ({
          value: status,
          label: this.$t(`ncrm.workflows.contactNormalization.status.${status}`),
        })),
      ];
    },
    headers() {
      return [
        { title: this.$t("ncrm.workflows.contactNormalization.headers.company"), key: "company_name", sortable: false },
        { title: this.$t("ncrm.workflows.contactNormalization.headers.status"), key: "status", sortable: false, width: 150 },
        { title: this.$t("ncrm.workflows.contactNormalization.headers.original"), key: "original", sortable: false },
        { title: this.$t("ncrm.workflows.contactNormalization.headers.proposed"), key: "proposed", sortable: false },
        { title: this.$t("ncrm.workflows.contactNormalization.headers.issues"), key: "issues", sortable: false, width: 90 },
        { title: this.$t("ncrm.workflows.contactNormalization.headers.updatedAt"), key: "updated_at", sortable: false, width: 168 },
        { title: "", key: "actions", sortable: false, align: "end", width: 112 },
      ];
    },
  },
  watch: {
    workflowInstanceId: {
      immediate: true,
      handler() {
        this.resetAndFetch();
      },
    },
    nodeKey() {
      this.resetAndFetch();
    },
    statusFilter() {
      this.page = 1;
      this.selectedIds = [];
      this.fetchRecords();
    },
    page() {
      this.fetchRecords();
    },
    perPage() {
      this.page = 1;
      this.fetchRecords();
    },
  },
  methods: {
    ...contactNormalizationQueueActions,
    canApplyStatus(status) {
      return status === "ready";
    },
  },
};
</script>

<style scoped>
.ncrm-contact-normalization__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
}

.ncrm-contact-normalization__status-filter {
  width: 190px;
}

.ncrm-contact-normalization__bulk {
  align-items: center;
  background: rgba(var(--v-theme-primary), 0.08);
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin: 12px 16px 0;
  padding: 8px 12px;
  border-radius: 8px;
}

.ncrm-contact-normalization__value-list {
  max-width: 360px;
  overflow-wrap: anywhere;
}

@media (max-width: 780px) {
  .ncrm-contact-normalization__header {
    flex-direction: column;
  }

  .ncrm-contact-normalization__status-filter {
    width: 100%;
  }
}
</style>
